---
title: Tunnels
lede: A public URL onto a port a session serves, for webhooks and sign-in callbacks. Off by default, closed with the session.
order: 41
part: Shape the machine
---

A tunnel publishes one port of one session at a public URL, through ngrok. Use it when something outside must reach your work: an Auth0 callback, a Stripe webhook, a sender that has to reach you.

:::warn Anyone with the URL gets in
While a tunnel is open, anybody with its URL reaches that port of that sandbox, and no egress allowlist applies to what arrives through it. Tunnels are off by default, opened one project at a time, and closed when the session ends.
:::

### Setting up ngrok

![ngrok settings](media/settings-tunnels.webp)

1. Open **Settings › Add an integration › ngrok**.
2. **The authtoken**: the page walks you through it. **Sign up on ngrok** creates a free account if you have none; **Open authtoken page** opens [dashboard.ngrok.com/get-started/your-authtoken](https://dashboard.ngrok.com/get-started/your-authtoken), where you press **Copy**; paste it and press **Keep it**. It is kept in the OS keychain, never put on a command line or in a sandbox, and checked by ngrok when the first tunnel opens.
3. **Public URLs**: **Turn them on**. This is the default every workspace inherits.

ngrok then has its own row under Integrations. Its page shows how many tunnels are open, and lets you **Replace** the authtoken or **Turn off** public URLs. **Remove this integration** forgets the authtoken, closes every tunnel and turns public URLs off.

### The ngrok agent

Airlock uses its own pinned copy of ngrok, kept in `~/.airlock/bin` and checked against its SHA-256 before each run. The first tunnel downloads it; **Install now** does it sooner, and **Reinstall** replaces it.

An ngrok you installed yourself is run only if you press **Use my own** and give its path. Airlock then only runs it and never installs or updates it. Its own authtoken is never read.

### Opening a tunnel

Click the globe on a project's row in [Projects & logs](/projects). It is there when the workspace allows tunnels, ngrok has an authtoken and the repository declares a port. The row shows the public address while the tunnel is open; click the address to copy it. The tunnel closes with the session. The session's agent can ask for one too; it opens only when you accept the agent's card.

### When a tunnel will not open

A tunnel that fails stays on the project's row: the globe turns red, its tooltip says why, and clicking it tries again. A project that opens its tunnel by itself still starts without one.

**The endpoint is already online (`ERR_NGROK_334`)**: ngrok already serves that domain for your account. Airlock tries again for about 13 seconds, since ngrok can hold a domain for a few seconds after a tunnel closes. Before the first wait, on macOS and Linux, it also stops an ngrok it left behind after a crash, and only one it can prove is its own: started from the same path, with Airlock's config and that domain, and with no parent left.

If it still fails, the holder is one of these:

- another computer signed in with the same authtoken;
- an ngrok you started yourself;
- on Windows, an ngrok left by a crash: Airlock does not look for one there, so end `ngrok.exe` in Task Manager.

[dashboard.ngrok.com/endpoints](https://dashboard.ngrok.com/endpoints) lists what is online on your account. Stop it there or on the computer running it, then click the globe.

### Per workspace

![Workspace tunnel settings](media/ws-tunnels.webp)

**Workspace settings › Tunnels**:

- **Public URLs for this workspace** shows the application's answer; **Override** makes it this workspace's own. Off means no ngrok is looked for, no credential is read, and the dock offers nothing to open.
- **Provider** says whether ngrok is installed and signed in, and lets you set it up if not.
- **What can be published** lists the repositories that declare a port.

Each repository's page in **Repositories** has two settings, shown once the workspace allows tunnels:

| Setting | What it does |
|---|---|
| **Tunnel domain** | A domain reserved with ngrok, such as `orders-dev.ngrok.app`, so the URL stays the same across sessions. Needed for a webhook or callback you register once. Empty, each session gets a random one |
| **Serve through the tunnel** | Opens the tunnel whenever the project starts, and gives the process its public address as `AIRLOCK_TUNNEL_URL` |
